import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Globe2, Plus, Trash2 } from "lucide-react";
import type { Dispatch, SetStateAction } from "react";

interface Registry {
  id: string;
  name: string;
  description: string;
  url: string;
  isEditing?: boolean;
  draft?: Pick<Registry, "name" | "description" | "url">;
}

interface RegistriesTabProps {
  registries: Registry[];
  setRegistries: Dispatch<SetStateAction<Registry[]>>;
  newRegistry: { name: string; description: string; url: string };
  setNewRegistry: Dispatch<SetStateAction<{ name: string; description: string; url: string }>>;
  canAdd: boolean;
}

export const RegistriesTab = ({
  registries,
  setRegistries,
  newRegistry,
  setNewRegistry,
  canAdd,
}: RegistriesTabProps) => {
  return (
    <div className="space-y-3 sm:space-y-4">
      <div className="flex flex-col gap-2 sm:gap-3 rounded-lg sm:rounded-xl border border-border/70 bg-white/80 p-3 sm:p-4 shadow-sm dark:bg-white/5">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h4 className="text-xs sm:text-sm font-semibold text-foreground">MCP Registries</h4>
            <p className="text-[10px] sm:text-xs text-muted-foreground">
              Connect to registries to discover MCP servers and tools.
            </p>
          </div>
          <Button
            variant="secondary"
            size="sm"
            className="gap-1 sm:gap-2 h-7 sm:h-8 text-xs"
            disabled={!canAdd}
            onClick={() => {
              setRegistries(prev => [
                ...prev,
                {
                  id: `reg-${Date.now()}`,
                  name: newRegistry.name,
                  description: newRegistry.description,
                  url: newRegistry.url,
                },
              ]);
              setNewRegistry({ name: "", description: "", url: "" });
            }}
          >
            <Plus className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="hidden sm:inline">Add registry</span>
            <span className="sm:hidden">Add</span>
          </Button>
        </div>
        <div className="grid gap-2 sm:grid-cols-[1fr,1fr,2fr]">
          <Input
            placeholder="Registry name"
            value={newRegistry.name}
            onChange={e => setNewRegistry(prev => ({ ...prev, name: e.target.value }))}
            className="h-8 sm:h-9 text-xs sm:text-sm"
          />
          <Input
            placeholder="https://registry.example.com"
            value={newRegistry.url}
            onChange={e => setNewRegistry(prev => ({ ...prev, url: e.target.value }))}
            className="h-8 sm:h-9 text-xs sm:text-sm"
          />
          <Input
            placeholder="Description"
            value={newRegistry.description}
            onChange={e => setNewRegistry(prev => ({ ...prev, description: e.target.value }))}
            className="h-8 sm:h-9 text-xs sm:text-sm"
          />
        </div>
      </div>
      <div className="space-y-2">
        {registries.map(reg => (
          <div
            key={reg.id}
            className="flex flex-col gap-2 sm:gap-3 rounded-lg sm:rounded-xl border border-border/70 bg-white/80 p-3 sm:p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md dark:bg-white/5 sm:flex-row sm:items-center sm:justify-between"
          >
            {reg.isEditing ? (
              <>
                <div className="flex-1 space-y-2">
                  <div className="grid gap-2 sm:grid-cols-[1fr,1fr]">
                    <Input
                      value={reg.draft?.name ?? reg.name}
                      onChange={e =>
                        setRegistries(prev =>
                          prev.map(item =>
                            item.id === reg.id
                              ? { ...item, draft: { ...(item.draft ?? item), name: e.target.value } }
                              : item,
                          ),
                        )
                      }
                      placeholder="Registry name"
                      className="h-8 sm:h-9 text-xs sm:text-sm"
                    />
                    <Input
                      value={reg.draft?.url ?? reg.url}
                      onChange={e =>
                        setRegistries(prev =>
                          prev.map(item =>
                            item.id === reg.id
                              ? { ...item, draft: { ...(item.draft ?? item), url: e.target.value } }
                              : item,
                          ),
                        )
                      }
                      placeholder="https://registry.example.com"
                      className="h-8 sm:h-9 text-xs sm:text-sm"
                    />
                  </div>
                  <Input
                    value={reg.draft?.description ?? reg.description}
                    onChange={e =>
                      setRegistries(prev =>
                        prev.map(item =>
                          item.id === reg.id
                            ? { ...item, draft: { ...(item.draft ?? item), description: e.target.value } }
                            : item,
                        ),
                      )
                    }
                    placeholder="Description"
                    className="h-8 sm:h-9 text-xs sm:text-sm"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    className="h-7 sm:h-8 text-xs"
                    onClick={() =>
                      setRegistries(prev =>
                        prev.map(item =>
                          item.id === reg.id
                            ? {
                              ...item,
                              ...item.draft,
                              draft: undefined,
                              isEditing: false,
                            }
                            : item,
                        ),
                      )
                    }
                  >
                    Save
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 sm:h-8 text-xs"
                    onClick={() =>
                      setRegistries(prev =>
                        prev.map(item =>
                          item.id === reg.id ? { ...item, draft: undefined, isEditing: false } : item,
                        ),
                      )
                    }
                  >
                    Cancel
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="space-y-0.5 sm:space-y-1 min-w-0">
                  <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-semibold text-foreground">
                    <Globe2 className="h-3 w-3 sm:h-4 sm:w-4 text-primary flex-shrink-0" />
                    <span className="truncate">{reg.name}</span>
                  </div>
                  <p className="text-[10px] sm:text-xs text-muted-foreground line-clamp-2">{reg.description}</p>
                  <p className="text-[9px] sm:text-xs font-mono text-muted-foreground truncate">{reg.url}</p>
                </div>
                <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
                  <Button variant="secondary" size="sm" className="gap-1 sm:gap-2 h-7 sm:h-8 text-xs">
                    <span className="hidden sm:inline">Connect</span>
                    <span className="sm:hidden">Go</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 sm:h-8 text-xs"
                    onClick={() =>
                      setRegistries(prev =>
                        prev.map(item =>
                          item.id === reg.id
                            ? {
                              ...item,
                              isEditing: true,
                              draft: { name: reg.name, description: reg.description, url: reg.url },
                            }
                            : item,
                        ),
                      )
                    }
                  >
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 sm:h-8 sm:w-8"
                    onClick={() => setRegistries(prev => prev.filter(item => item.id !== reg.id))}
                    aria-label="Remove registry"
                  >
                    <Trash2 className="h-3 w-3 sm:h-4 sm:w-4 text-rose-500" />
                  </Button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
